/* The cockpit's half of the sign-in, which is deliberately almost nothing.
 *
 * The whole flow lives in the Python service: it redirects to Entra ID, handles
 * the callback, and seals the result into an HttpOnly cookie. That cookie is
 * unreadable from here — which is the point, and the reason an XSS in a screen
 * that renders operator-supplied case text cannot walk off with a credential.
 * See `docs/adr/0004-bff-authentication.md`.
 *
 * So there are exactly three operations: ask the service who is signed in, and
 * navigate to the two endpoints that start and end a session. Signing in and
 * out are browser navigations rather than `fetch` because they are top-level
 * navigations into Microsoft's domain — there is no response for the cockpit to
 * read, and a redirect chain followed by `fetch` would end at an opaque
 * response instead of at a sign-in page.
 *
 * A store rather than component state because the session is one fact about the
 * application, read by the guard, the top bar and the sign-in page. Built on
 * the `useSyncExternalStore` contract so React subscribes to it directly.
 */

import { ApiError, apiUrl, get, request } from '@/shared/api/http/client';

/** Who is signed in, as the service is willing to say it.
 *
 * Hand-typed rather than generated, and it is the only shape in the cockpit that
 * is: `/auth/*` is declared `include_in_schema=False` on the API, because the
 * sign-in flow is a browser journey rather than an interface anybody integrates
 * against, so it does not appear in `contracts/openapi.json` for
 * `openapi-typescript` to read.
 *
 * No object id and no session id — `/auth/me` does not send them. The tenant
 * *is* here, but only as a name and a yes-or-no: the id stays in the sealed
 * cookie, because which rows a request sees is the service's decision and
 * nothing the browser holds may influence it. What the cockpit needs to know is
 * whether a choice has been made, so it can offer the picker instead of a screen
 * with no data on it.
 */
export interface SessionUser {
  readonly name: string;
  readonly email: string;
  readonly roles: readonly string[];
  /** False between signing in and choosing a tenant. Every guarded read answers
      403 `tenantNotSelected` in that window, which is a state the cockpit has a
      screen for rather than an error. */
  readonly tenantSelected: boolean;
  /** The chosen tenant's name, or null before the choice. Never its id. */
  readonly tenantName: string | null;
}

/** What `POST /auth/tenant` answers with once the choice is sealed. */
interface SelectedTenant {
  readonly id: string;
  readonly name: string;
}

/** `pending` is the load-bearing one.
 *
 * Before `/auth/me` answers, "is this person signed in?" is not "no", it is
 * "not yet" — and treating it as "no" would redirect to `/login` in the middle
 * of the first paint after a successful callback, which cancels nothing but
 * makes a signed-in operator watch the sign-in page flash past. */
export type SessionState =
  | { readonly status: 'pending'; readonly user: null }
  | { readonly status: 'authenticated'; readonly user: SessionUser }
  | { readonly status: 'anonymous'; readonly user: null };

const PENDING: SessionState = { status: 'pending', user: null };

let state: SessionState = PENDING;
const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function snapshot(): SessionState {
  return state;
}

/** Ask the service who is signed in. Safe to call more than once.
 *
 * A 401 is not an error here, it is the answer — nobody is signed in. Anything
 * else, including the service being unreachable, resolves to `anonymous` as
 * well: the cockpit cannot show a screen it cannot fetch data for, and a
 * sign-in page is a better place to be stranded than a half-rendered dashboard.
 */
export async function loadSession(): Promise<void> {
  publish(PENDING);

  try {
    const user = await get<SessionUser>('/auth/me');
    publish({ status: 'authenticated', user });
  } catch (cause) {
    if (!(cause instanceof ApiError)) throw cause;
    publish({ status: 'anonymous', user: null });
  }
}

/** Seal the chosen tenant into the session cookie, and record that here.
 *
 * The service re-issues the cookie carrying the tenant alongside the identity
 * that was already in it — so this is a write, not a client-side preference, and
 * a refusal is a real refusal: an operator who posts a tenant they hold no grant
 * for is answered 403 by the same check every guarded read runs, not by a hidden
 * option in a menu.
 *
 * The `ApiError` is left to propagate. The caller is the picker screen, which
 * has somewhere to put the reason; swallowing it here would leave an operator
 * clicking a tenant that silently does nothing.
 *
 * The snapshot is updated from the response rather than from the argument. What
 * the cockpit shows in the top bar has to be the tenant the *cookie* now names,
 * and only the service can say what that is.
 */
export async function chooseTenant(customerId: string): Promise<SelectedTenant> {
  const { data } = await request<SelectedTenant>('POST', '/auth/tenant', {
    body: { customerId },
  });

  const current = snapshot();
  if (current.status === 'authenticated') {
    publish({
      status: 'authenticated',
      user: { ...current.user, tenantSelected: true, tenantName: data.name },
    });
  }
  return data;
}

/** Hand the browser to the service, which hands it to Entra ID.
 *
 * `next` comes back on the other side of the redirect, so an operator who
 * followed a link to a case lands on that case rather than on the dashboard.
 * The service restricts it to a same-site path before using it. */
export function beginLogin(next = '/'): void {
  window.location.assign(apiUrl(`/auth/login?next=${encodeURIComponent(next)}`));
}

/** End the session here and at Entra ID.
 *
 * Also a navigation, and for a reason worth stating: clearing the cookie alone
 * would leave the directory session standing, so the next sign-in would
 * complete without a prompt and look — from the machine — as though nobody had
 * signed out. */
export function beginLogout(): void {
  /* Clear protected content before leaving. Browser history can restore an SPA
     document from the back/forward cache without remounting React; keeping the
     authenticated snapshot would then briefly reveal the previous screen even
     though the HttpOnly session cookie has already been deleted. */
  publish({ status: 'anonymous', user: null });
  /* Replace the current protected entry rather than leaving it immediately
     behind the Entra logout journey. Older SPA entries are covered by the
     anonymous snapshot above and the pageshow revalidation in AuthProvider. */
  window.location.replace(apiUrl('/auth/logout'));
}
